import { cn } from '@/lib/utils'
import { type LegacyPlan, getAnnualPricePerMonth } from '@/stores/user-store'
import { PlanBadge } from './plan-badge'
import { PriceDisplay } from './price-display'
import { BillingToggle } from './billing-toggle'
import { FeatureList } from './feature-list'
import { SubscribeButton } from './subscribe-button'

interface PlanCardProps {
  plan: LegacyPlan
  isCurrentPlan: boolean
  onCryptoPurchase: () => void
  onTelegramPurchase: () => void
  onCardPurchase?: () => void
  cryptoLoading?: boolean
  telegramLoading?: boolean
  isAnnual?: boolean
  onBillingToggle?: () => void
  isDowngrade?: boolean
  isUltimate?: boolean
  className?: string
}

export function PlanCard({
  plan,
  isCurrentPlan,
  onCryptoPurchase,
  onTelegramPurchase,
  onCardPurchase,
  cryptoLoading = false,
  telegramLoading = false,
  isAnnual = false,
  onBillingToggle,
  isDowngrade = false,
  isUltimate = false,
  className,
}: PlanCardProps) {
  const isPopular = plan.id === 'creator'
  const displayPrice = isAnnual ? getAnnualPricePerMonth(plan.annualPrice) : plan.monthlyPrice

  return (
    <div
      className={cn(
        'relative flex flex-col p-5 rounded-2xl glass transition-all',
        !isUltimate && 'h-full',
        isPopular && 'border-violet-500/40',
        isCurrentPlan && 'ring-2 ring-violet-500/50',
        isUltimate && 'md:flex-row md:items-start md:gap-8',
        className
      )}
    >
      {isPopular && (
        <PlanBadge
          variant="popular"
          className="absolute -top-3 left-1/2 -translate-x-1/2 md:left-auto md:right-4 md:translate-x-0"
        />
      )}
      {isUltimate && (
        <PlanBadge
          variant="premium"
          className="absolute -top-3 right-4 md:top-4"
        />
      )}

      <div className={cn('flex flex-col', isUltimate ? 'md:flex-1' : 'flex-1')}>
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-semibold text-white text-base sm:text-lg">The {plan.name}</span>
          </div>
          <PriceDisplay price={displayPrice} annualTotal={isAnnual ? plan.annualPrice : undefined} />
        </div>
        {onBillingToggle && (
          <BillingToggle isAnnual={isAnnual} onToggle={onBillingToggle} />
        )}
        <FeatureList features={plan.features} columns={isUltimate} />
      </div>

      <div className={cn(isUltimate ? 'mt-4 md:w-64 md:shrink-0 md:self-end md:mt-0' : 'mt-auto')}>
        <SubscribeButton
          onCryptoPurchase={onCryptoPurchase}
          onTelegramPurchase={onTelegramPurchase}
          onCardPurchase={onCardPurchase}
          isCurrentPlan={isCurrentPlan}
          isDowngrade={isDowngrade}
          cryptoLoading={cryptoLoading}
          telegramLoading={telegramLoading}
        />
      </div>
    </div>
  )
}
